import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Copy,
  MoreHorizontal,
  Trash2Icon,
  Files,
  ClipboardCopy,
  ClipboardPaste,
  ScanEye,
  Logs,
  Folder,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';

interface FloatingControlBarProps {
  onCopyNode: (id: string) => void;
  onDuplicateNode: (id: string) => void;
  onNodesEdgesDelete: (params: { nodeId: string }) => void;
  nodeId: string;
  /** Handler to open the output preview panel */
  onOpenPreview?: () => void;
  /** Handler to open the node events/status panel */
  onOpenNodeStatus?: () => void;
  /** Handler to open the files preview panel */
  onOpenFilesPreview?: () => void;
  /** Whether to show the Output and Events action buttons */
  shouldShowViewButtons?: boolean;
  enableOutputButton?: boolean;
  enableEventsButton?: boolean;
  enableFilesPreview?: boolean;
  isLocked?: boolean;
  /** Hide output button completely */
  hideOutputButton?: boolean;
  /** Catalog type name (e.g. "Agent"); the instance struct may be a stale snapshot of the catalog entry */
  catalogTypeName?: string;
  /** Catalog node id (e.g. "agent_node"), shown as a hover hint. */
  catalogNodeId?: string;
}

export const FloatingControlBar: React.FC<FloatingControlBarProps> = ({
  onCopyNode,
  onDuplicateNode,
  onNodesEdgesDelete,
  nodeId,
  onOpenPreview,
  onOpenNodeStatus,
  onOpenFilesPreview,
  shouldShowViewButtons = false,
  enableOutputButton = false,
  enableEventsButton = false,
  enableFilesPreview = false,
  isLocked = false,
  hideOutputButton = false,
  catalogTypeName,
  catalogNodeId,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <div className='floating-control-bar'>
      {/* Output and Events buttons */}
      {shouldShowViewButtons && (
        <>
          {/* Hide Output button for file_input nodes */}
          {!hideOutputButton && (
            <Button
              variant='ghost'
              size='sm'
              className='control-section px-2 py-1 h-auto'
              onClick={(e) => {
                e.stopPropagation();
                onOpenPreview?.();
              }}
              disabled={!enableOutputButton}
              title='View Output'
            >
              <ScanEye className='w-4 h-4' />
              <span className='text-sm font-medium text-gray-700 dark:text-white'>
                Output
              </span>
            </Button>
          )}
          <Button
            variant='ghost'
            size='sm'
            className='control-section px-2 py-1 h-auto'
            onClick={(e) => {
              e.stopPropagation();
              onOpenNodeStatus?.();
            }}
            disabled={!enableEventsButton}
            title='View Events'
          >
            <Logs className='w-4 h-4' />
            <span className='text-sm font-medium text-gray-700 dark:text-white'>
              Events
            </span>
          </Button>
          {enableFilesPreview && (
            <Button
              variant='ghost'
              size='sm'
              className='control-section px-2 py-1 h-auto'
              onClick={(e) => {
                e.stopPropagation();
                onOpenFilesPreview?.();
              }}
              title='View Files'
            >
              <Folder className='w-4 h-4' />
              <span className='text-sm font-medium text-gray-700 dark:text-white'>
                Files
              </span>
            </Button>
          )}
          <div className='w-px h-6 bg-gray-300' />
        </>
      )}

      {/* Action Icons */}
      <div className='action-buttons'>
        {/* Separator */}
        <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              variant='ghost'
              size='sm'
              className='action-button flex items-center gap-1.5 whitespace-nowrap px-2 py-1 h-auto'
              title='More options'
            >
              <MoreHorizontal className='w-3 h-3 text-gray-800 dark:text-white shrink-0' />
              {!shouldShowViewButtons && (
                <span className='text-sm font-medium text-gray-700 dark:text-white'>
                  Action
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='w-56'>
            <DropdownMenuItem
              onClick={() => {
                if (!isLocked) onCopyNode(nodeId);
                setIsDropdownOpen(false);
              }}
              disabled={isLocked}
            >
              <Files className='mr-2 h-4 w-4 text-gray-800 dark:text-white' />
              <span>Copy Node</span>
              <DropdownMenuShortcut>⌘C</DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (!isLocked) onDuplicateNode(nodeId);
                setIsDropdownOpen(false);
              }}
              disabled={isLocked}
            >
              <Copy className='mr-2 h-4 w-4 text-gray-800 dark:text-white' />
              <span>Duplicate Node</span>
              <DropdownMenuShortcut>⌘D</DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                // onCopySettingsNode(nodeId);
                setIsDropdownOpen(false);
              }}
              disabled={true}
            >
              <ClipboardCopy className='mr-2 h-4 w-4 text-gray-800 dark:text-white' />
              <span>Copy Settings</span>
              <DropdownMenuShortcut>⇧⌘C</DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                // onPasteSettingsNode(nodeId);
                setIsDropdownOpen(false);
              }}
              disabled={true}
            >
              <ClipboardPaste className='mr-2 h-4 w-4 text-gray-800 dark:text-white' />
              <span>Paste Settings</span>
              <DropdownMenuShortcut>⇧⌘V</DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                if (!isLocked) onNodesEdgesDelete({ nodeId });
                setIsDropdownOpen(false);
              }}
              disabled={isLocked}
              className='hover:text-destructive focus:text-destructive'
            >
              <Trash2Icon className='mr-2 h-4 w-4 text-gray-800 dark:text-white' />
              <span>Delete</span>
              <DropdownMenuShortcut className='hover:text-destructive focus:text-destructive'>
                ⌫ / DEL
              </DropdownMenuShortcut>
            </DropdownMenuItem>
            {catalogTypeName && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel
                  className='py-1.5 text-xs font-normal text-muted-foreground'
                  title={catalogNodeId}
                >
                  Type: {catalogTypeName}
                </DropdownMenuLabel>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
